/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import qs from 'qs';
import {
  Asset,
  User
} from '../types/entities';
import {
  parseCollection as parseArcGISCollection
} from './ArcGISUtils';
import {
  createUser as _createUser,
  currentUser as _currentUser,
  deleteUser as _deleteUser,
  getAccount as _getAccount,
  getAsset as _getAsset,
  getUser as _getUser,
  logout as _logout,
  updateAsset as _updateAsset,
  updateUser as _updateUser
} from './Connect';
import {
  parse,
  parseCollection,
  parseExtendedAttributes,
  url
} from './ConnectUtils';
import {
  client,
  config,
  setToken
} from './SDConnectUtils';

/**
 * The authenticate function, as the name suggests, provides access to authenticating
 * with the CTC Platform via SDCTC Services.
 *
 * Note, this function implicitly sets the Authorization token which will automatically
 * be set for all subsequent requests.
 *
 * ```typescript
 *    import {authenticate} from '@ion/core/src/services/Connect';
 *
 *    const user = await authenticate(username, password);
 *
 * ```
 */
export const authenticate = async(username: string, password: string): Promise<User> => {
  const result = await client({
    url     : url('/login'),
    method  : 'post',
    headers : {
      ...config.headers,
      'Content-Type' : 'application/x-www-form-urlencoded;charset=UTF-8'
    },
    data : qs.stringify({
      username,
      password
    })
  });
  setToken(parse(result, 'authToken') as unknown as string);
  return parse(result, 'user');
};

/**
 * Retrieves the authenticated CTC user entity via SDConnect Services. Note, this
 * function is primarily used for facilitating auto-refresh as the authentication
 * API provides a consolidation of the authentication and currentUser services
 * into a single transaction.
 *
 * ```typescript
 *    import {currentUser} from '@ion/core/src/services/SDConnect';
 *
 *    const user = await currentUser(persistedAuthToken);
 *
 * ```
 */
export const currentUser = async(token: string): Promise<User> => {
  setToken(token);
  return _currentUser(client);
};

/**
 * The logout method, as the name suggests, provides access to signing out of the CTC
 * Platform via SDCTC Services.
 *
 * ```typescript
 *    import {logout} from '@ion/core/src/services/Connect';
 *
 *    const auth = await logout();
 *    // reload application ...
 *
 * ```
 */
export const logout = async(): Promise<any> => {
  return _logout(client);
};

/**
 * The forgotPassword method, as the name suggests, send the reset password request to the CTC
 * server via SDCTC Services and get the reset password link in the user's associated mail.
 *
 * ```typescript
 *
 *  import {forgotPassword} from '@ion/core/src/services/SDConnect';
 *
 *  await forgotPassword(username);
 *
 * ```
 */
export const forgotPassword = async(username: string): Promise<any> => {
  const result = await client({
    url     : url(`/users/forgotpassword?username=${encodeURIComponent(username)}`),
    method  : 'get',
    headers : {
      ...config.headers,
      'Content-Type' : 'application/x-www-form-urlencoded;charset=UTF-8'
    }
  });
  return result;
};

/**
 * Provides the CTC Reset Password Service via SDCTC which allows existing users to reset their CTC
 * password and subsequently receive a confirmation email formatted for the given appKey.
 *
 * @param token the reset token to authenticate.
 * @param password the password value.
 */
export const resetPassword = async(token: string, password: string): Promise<any> => {
  const result = await client({
    url     : url('/users/resetpassword'),
    method  : 'post',
    headers : {
      ...config.headers,
      'Content-Type' : 'application/x-www-form-urlencoded;charset=UTF-8'
    },
    data : qs.stringify({
      token,
      password
    })
  });
  return result;
};

/**
 * Retrieves all assets associated with the current user.
 *
 * ```typescript
 *    import {getAssets} from '@ion/core/src/services/SDConnect';
 *
 *    const assets = await getAssets();
 *
 * ```
 */
export const getAssets = async(): Promise<any> => {
  const result = await client.get(url('/assets'));
  return parseCollection(result, 'asset');
};

/**
 * Retrieves the asset current state associated with the specified asset ID.
 *
 * ```typescript
 *    import {getAssetCurrentState} from '@ion/core/src/services/SDConnect';
 *
 *    const assets = await getAssetCurrentState(assetId);
 *
 * ```
 */
export const getAssetCurrentState = async(assetId: number): Promise<any> => {
  const result = await client.get(url(`/asset/${assetId}/currentState`));
  return result?.data;
};

/**
 * Retrieves the asset information associated with the specified asset ID.
 *
 * ```typescript
 *    import {getAsset} from '@ion/core/src/services/SDConnect';
 *
 *    const asset = await getAsset(assetId);
 *
 * ```
 *
 * @param   The id of the asset to which is to be retrieved.
 * @returns The retrieved Asset entity.
 */
export const getAsset = async(assetId: number): Promise<any> => {
  return _getAsset(assetId, client);
};

/**
 * The updateAsset function update an existing Asset Entity within the CTC Platform.
 *
 * ```typescript
 *
 *     import {updateAsset} from '@ion/core/src/services/Connect';
 *
 *     const updated = await updateAsset(asset);
 *
 * ```
 *
 * @param   asset The CTC Asset entity which is to be updated.
 * @returns The updated Asset entity.
 */
export const updateAsset = async(asset: Asset): Promise<Asset> => {
  return _updateAsset(asset, client);
};

/**
 * The getUser function retrieves an existing User Entity from the CTC Platform.
 *
 * ```typescript
 *
 *     import {getUser} from '@ion/core/src/services/Connect';
 *
 *     const user = await getUser(id);
 *
 * ```
 *
 * @param   The id of the CTC User entity which is to be retrieved.
 * @returns The retrieved User entity.
 */
export const getUser = async(id: number): Promise<User> => {
  return _getUser(id, client);
};

/**
 * Retrieves all users associated with the current account
 * .
 *
 * ```typescript
 *    import {getUsers} from '@ion/core/src/services/SDConnect';
 *
 *    const users = await getUsers();
 *
 * ```
 */
export const getUsers = async(): Promise<any> => {
  const result = await client.get(url('/users'));
  return parseCollection(result, 'user');
};

/**
 * The createUser function creates a new User Entity within the CTC Platform.
 *
 * ```typescript
 *
 *     import {createUser} from '@ion/core/src/services/Connect';
 *
 *     const created = await createUser(user);
 *
 * ```
 *
 * @param    user The CTC User entity which is to be created.
 * @returns  The newly created User entity.
 */
export const createUser = async(user: User): Promise<User> => {
  return _createUser(user, client);
};

/**
 * The updateUser function updates an existing User Entity within the CTC Platform.
 *
 * ```typescript
 *
 *     import {updateUser} from '@ion/core/src/services/Connect';
 *
 *     const updated = await updateUser(user);
 *
 * ```
 * @param   user The CTC User entity which is to be updated.
 * @returns The updated User entity.
 */
export const updateUser = async(user: User): Promise<User> => {
  return _updateUser(user, client);
};

/**
 * The deleteUser function deletes an existing User Entity from the CTC Platform.
 *
 * ```typescript
 *
 *     import {deleteUser} from '@ion/core/src/services/Connect';
 *
 *     const deleted = await deleteUser(user);
 *
 * ```
 *
 * @param   user The CTC User entity which is to be deleted.
 * @returns The deleted User entity or null if not found.
 */
export const deleteUser = async(user: User): Promise<User> => {
  return _deleteUser(user, client);
};
/**
 * The updateUserPassword function updates an existing User Entity's password
 * within the CTC Platform.
 *
 * ```typescript
 *
 *     import {updateUserPassword} from '@ion/core/src/services/Connect';
 *
 *     const updated = await updateUserPassword(userId, password);
 *
 * ```
 * @param   userId The ID of the CTC User entity which is to be updated.
 * @param   password The new password for the user.
 */
export const updateUserPassword = async(userId: number, password: any): Promise<User> => {
  const results = await client.put(url(`/users/${userId}/password`), JSON.stringify(password));
  return parse(results, 'user') as User;
};

/**
 * Retrieves the current asset location for the specified asset ID.
 *
 * ```typescript
 *    import {getAssetLocation} from '@ion/core/src/services/SDConnect';
 *
 *    const location = await getAssetLocation(assetId);
 *
 * ```
 */
export const getAssetLocation = async(assetId: number): Promise<any> => {
  const query = qs.stringify({
    where            : `assetId IN (${assetId})`,
    outFields        : '*',
    returnGeometry   : 'true',
    returnTrueCurves : 'false',
    f                : 'pjson'
  });
  const result = await client.get(url(`/cvs?${query}`));
  return parseArcGISCollection(result, ['attributes', 'geometry']);
};

/**
 * Retrieves the device information associated with the specified device ID.
 *
 * ```typescript
 *    import {getDevice} from '@ion/core/src/services/SDConnect';
 *
 *    const device = await getDevice(deviceId);
 *
 * ```
 */
export const getDevice = async(deviceId: number): Promise<any> => {
  const result = await client.get(url(`/devices/${deviceId}`));
  return parse(result, 'device');
};

/**
 * Retrieves the account information associated with the specified account ID.
 *
 * ```typescript
 *    import {getAccount} from '@ion/core/src/services/SDConnect';
 *
 *    const account = await getAccount(accountId);
 *
 * ```
 */
export const getAccount = async(accountId: number): Promise<any> => {
  return _getAccount(accountId, client);
};

/**
 * Retrieves the current EULA from the server.
 *
 * ```typescript
 *    import {getEULA} from '@ion/core/src/services/SDConnect';
 *
 *    const eula = await getEULA();
 *
 * ```
 */
export const getEULA = async(contentOnly: boolean = false): Promise<any> => {
  const result = await client.get(url('/eula'));
  if (contentOnly) {
    const body    = /<body>([\s\S]*?)<script/g.exec(result?.data);
    const content = body?.[1];
    return content;
  }
  return result?.data;
};

/**
 * Sends an acceptance of the EULA to the server.
 *
 * ```typescript
 *    import {acceptEULA} from '@ion/core/src/services/SDConnect';
 *
 *    const eula = await acceptEULA();
 *
 * ```
 */
export const acceptEULA = async(countryCode: string, userId: number): Promise<any> => {
  let user = {};
  let eulaAccepted = false;
  const response = await client.post(url(`/eula/accept?countryCd=${countryCode}`));
  if (response) {
    const results = parseCollection(response, 'extendedAttribute');
    if (results?.length) {
      const extendedAttribute = results[0];
      extendedAttribute.value = parseExtendedAttributes({extendedAttribute}, true);
      eulaAccepted = extendedAttribute?.value?.eulaAccepted;
      if (eulaAccepted) {
        user = await getUser(userId);
      }
    }
  }
  return {
    user         : user,
    eulaAccepted : eulaAccepted
  };
};

/**
 * Retrieves the list of subscription plans for the current user.
 *
 * ```typescript
 *    import {getSubscriptionPlans} from '@ion/core/src/services/SDConnect';
 *
 *    const plans = await getSubscriptionPlans(vin);
 *
 * ```
 */
export const getSubscriptionPlans = async(vin: string): Promise<any> => {
  const result = await client.get(url(`/salesforce/subscriptions/plans?vin=${vin}`));
  return result?.data;
};

/**
 * Retrieves a subscription response from the server.
 *
 * ```typescript
 *    import {getSubscription} from '@ion/core/src/services/SDConnect';
 *
 *    const subscription = await getSubscriptions();
 *
 * ```
 */
export const getSubscriptions = async(): Promise<any> => {
  const result = await client.get(url('/salesforce/subscriptions'));
  return result?.data;
};

/**
 * Creates a payment plan URL that can subsequently be used to launch a payment window.
 *
 * ```typescript
 *    import {createSubscriptionPaymentUrl} from '@ion/core/src/services/SDConnect';
 *
 *    const url = await createSubscriptionPaymentPlanUrl(vin, planId, subscriptionId);
 *
 * ```
 */
export const createSubscriptionPlanUrl = async(vin: string, planId: string, subscriptionId: string): Promise<any> => {
  const result = await client.post(url('/salesforce/subscriptions/plans/paymenturl'), {vin, planId, subscriptionId});
  return result?.data?.paymentUrl;
};

/**
 * Retrieves a settings response from the server.
 *
 * ```typescript
 *    import {getSettings} from '@ion/core/src/services/SDConnect';
 *
 *    const settings = await getSettings();
 *
 * ```
 */
export const getSettings = async(): Promise<any> => {
  const result = await client.get(url('/settings'));
  return parse(result, 'settings');
};
